import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import payableCreditsApi from '../../../api/payableCreditsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import PayableCreditFormDialog from '../../../sections/admin/finance/PayableCreditFormDialog';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency: currency || 'PEN' }).format(
    value || 0
  );

const STATUS_META = {
  pendiente: { label: 'Pendiente', color: 'default' },
  parcial: { label: 'Parcial', color: 'warning' },
  pagado: { label: 'Pagado', color: 'success' },
  vencido: { label: 'Vencido', color: 'error' },
};

export default function PayableCreditsListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [creditToDelete, setCreditToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await payableCreditsApi.getPayableCredits({
        search: search || undefined,
        status: statusFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los créditos por pagar.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const handleConfirmDelete = async () => {
    if (!creditToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await payableCreditsApi.deletePayableCredit(creditToDelete.id);
      enqueueSnackbar('Crédito eliminado correctamente.', { variant: 'success' });
      setCreditToDelete(null);
      await fetchCredits();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el crédito.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'description',
        headerName: 'Proveedor / descripción',
        flex: 1,
        minWidth: 180,
        valueGetter: (value) => value || '—',
      },
      {
        field: 'invoice_date',
        headerName: 'Facturación',
        width: 110,
        valueFormatter: (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'due_date',
        headerName: 'Vencimiento',
        width: 110,
        valueFormatter: (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'amount',
        headerName: 'Monto',
        width: 130,
        valueGetter: (value, row) => formatMoney(row.amount, row.currency),
      },
      {
        field: 'is_installment',
        headerName: 'Tipo',
        width: 100,
        renderCell: (params) => (
          <Chip label={params.value ? 'Plazos' : 'Contado'} size="small" variant="outlined" />
        ),
      },
      {
        field: 'status',
        headerName: 'Estado',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={STATUS_META[params.value]?.label || params.value}
            color={STATUS_META[params.value]?.color || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => {
              setEditingCredit(params.row);
              setFormOpen(true);
            }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setCreditToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row" spacing={2}>
        <div>
          <Typography variant="h4">Créditos por pagar</Typography>
          <Typography variant="body2" color="text.secondary">
            Compras a crédito (ej. a un proveedor, con una letra) que la tienda debe pagar: fecha de facturación,
            vencimiento, monto, adjunto de la factura y, si es a plazos, el checklist de cuotas.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingCredit(null);
            setFormOpen(true);
          }}
        >
          Nuevo crédito
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Proveedor / descripción"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowClick={(params) => {
            setEditingCredit(params.row);
            setFormOpen(true);
          }}
        />
      </Card>

      <PayableCreditFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchCredits}
        credit={editingCredit}
      />

      <Dialog open={Boolean(creditToDelete)} onClose={() => setCreditToDelete(null)}>
        <DialogTitle>Eliminar crédito</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar el crédito{' '}
            <strong>{creditToDelete?.description || `N.° ${creditToDelete?.id}`}</strong>? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
